import React, { useRef, useEffect } from "react";
import MessageBubble from "./MessageBubble";
import SuggestionCard from "./SuggestionCard";
import { ConversationItem, ThemeConfig } from "../hooks/useVoiceWidget";
import { getStrings, Language } from "../i18n/strings";
import { NEUTRAL_PALETTE } from "../utils/neutralPalette";

interface MessageListProps {
  conversationItems: ConversationItem[];
  isAgentSpeaking: boolean;
  /** True when user sent a message but no agent response has arrived yet */
  isAgentThinking?: boolean;
  avatarUrl?: string;
  widgetName?: string | null;
  welcomeMessage?: string | null;
  theme?: Partial<ThemeConfig>;
  uiVariant?: "legacy" | "split-switch";
  /** When false, agent link cards render without a clickable button. */
  linkButtonsEnabled?: boolean;
  language?: Language;
}

const MessageList: React.FC<MessageListProps> = ({
  conversationItems,
  isAgentSpeaking,
  isAgentThinking = false,
  avatarUrl,
  widgetName,
  welcomeMessage,
  theme,
  uiVariant = "legacy",
  linkButtonsEnabled = true,
  language,
}) => {
  const t = getStrings(language);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const useNeutralTextWindow = uiVariant === "split-switch";

  const scrollToBottom = () => {
    // Don't yank the viewport while the user is selecting text to copy.
    const selection = typeof window !== "undefined" ? window.getSelection() : null;
    if (selection && !selection.isCollapsed && selection.toString().length > 0) {
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversationItems, isAgentThinking]);

  // Sort items by timestamp
  const sortedItems = [...conversationItems].sort(
    (a, b) => a.timestamp - b.timestamp
  );

  const hasAgentMessages = sortedItems.some(
    (i) => i.type === "message" && i.message?.author === "agent"
  );

  // Show welcome only when there's no conversation at all AND we're not in a
  // live session (no user messages sent). Once the user sends a message, the
  // agent's own greeting will appear via onMessage, with no duplicate.
  const showWelcome = sortedItems.length === 0 && !isAgentThinking;
  const splitWelcomeBody = (welcomeMessage || t.greetingFollowup).replace(/^\s*\u{1F44B}\s*/u, "");

  return (
    <div
      className={useNeutralTextWindow ? "heyai-panel-section heyai-panel-section--body" : undefined}
      style={{
        flex: 1,
        overflowY: "auto",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      {showWelcome && useNeutralTextWindow ? (
        <div
          data-testid="heyai-welcome"
          style={{
            alignSelf: "center",
            margin: "auto 0",
            maxWidth: "300px",
            padding: "0 24px",
            textAlign: "center",
            fontSize: "14px",
            lineHeight: 1.45,
            color: "rgba(94, 93, 89, 0.82)",
          }}
        >
          {splitWelcomeBody}
        </div>
      ) : showWelcome ? (
        <div
          style={{
            textAlign: "center",
            color: useNeutralTextWindow ? NEUTRAL_PALETTE.anthracite : "#64748b",
            fontSize: "14px",
            marginTop: "40px",
          }}
        >
          {welcomeMessage ||
            (widgetName
              ? `${t.greetingHello} ${widgetName.split(" - ")[0]}.`
              : `${t.greetingHello} ${t.greetingTail}`)}
          <br />
          {t.greetingFollowup}
        </div>
      ) : (
        sortedItems.map((item, index) => (
          <div key={item.id} style={{ marginBottom: "0" }}>
            {item.type === "message" && item.message && (
              <MessageBubble
                message={item.message}
                isLastAgentMessage={
                  index === sortedItems.length - 1 &&
                  item.message.author === "agent"
                }
                isAgentSpeaking={isAgentSpeaking}
                avatarUrl={avatarUrl}
                theme={theme}
                uiVariant={uiVariant}
                language={language}
              />
            )}

            {item.type === "suggestion" && item.suggestion && (
              <SuggestionCard
                suggestion={item.suggestion}
                theme={theme}
                uiVariant={uiVariant}
                linkButtonsEnabled={linkButtonsEnabled}
              />
            )}
          </div>
        ))
      )}

      {/* Thinking indicator shown when user sent a message but agent has not responded yet. */}
      {isAgentThinking && !hasAgentMessages && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "0 4px",
          }}
        >
          <div
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              background: useNeutralTextWindow ? NEUTRAL_PALETTE.pearl : "#e2e8f0",
              border: useNeutralTextWindow ? `1px solid ${NEUTRAL_PALETTE.mist}` : "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "11px",
              fontWeight: useNeutralTextWindow ? 700 : 400,
              color: useNeutralTextWindow ? NEUTRAL_PALETTE.anthracite : "inherit",
              letterSpacing: useNeutralTextWindow ? "0.3px" : "normal",
            }}
          >
            AI
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "3px",
              color: useNeutralTextWindow ? NEUTRAL_PALETTE.slate : "#94a3b8",
              fontSize: "13px",
            }}
          >
            <span>{t.agentThinking}</span>
            <span className="thinking-dots" style={{ display: "inline-flex", gap: "2px" }}>
              <span style={{ animation: "dot-bounce 1.4s 0s infinite both" }}>.</span>
              <span style={{ animation: "dot-bounce 1.4s 0.2s infinite both" }}>.</span>
              <span style={{ animation: "dot-bounce 1.4s 0.4s infinite both" }}>.</span>
            </span>
          </div>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
